// Imports
import AxiosConfig from './serverAxiosConfig'
import SpyderAxios from './spyderAxiosConfig'
import firebase from '../config/FirebaseConfig'
import { createQuery } from '../../utils/filterUtils'

// ====================================================================
// ============================ SWIPEFILE =============================
// ====================================================================

const save = async (payload, isSpyderView = false) => {
  const { Aa: token } = firebase.auth().currentUser
  
  const { data } = await AxiosConfig.post(
    '/fs/ads/save',
    { ...payload, isSpyderView },
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )
  
  return data
}

const getLibraryAds = async (nextId, filters) => {
  // Pull the current token off the user
  const { Aa: token } = firebase.auth().currentUser

  const params = {
    // Create the filters
    ...createQuery(filters),
    next: nextId
  }

  const { data } = await AxiosConfig.get(
    '/ads/library',
    {
      params,
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )
  return data
}

// Fetch AD
const getBoardAds = async (boardId, nextId, filters) => {
  // Pull the current token off the user
  const { Aa: token } = firebase.auth().currentUser

  const params = {
    // Create the filters
    ...createQuery(filters),
    next: nextId
  }

  const { data } = await AxiosConfig.get(
    `/ads/board/${boardId}`,
    {
      params,
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )

  return data
}

const getBoardsAdCount = async (teamId) => {
  const user = firebase.auth().currentUser

  // If no user, don't query for ad count of a board
  if (!user) {
    return
  }
  const token = user.Aa

  const payload = {}
  if (teamId) {
    payload.teamId = teamId
  }

  const { data } = await AxiosConfig.post(
    '/ads/board/count',
    payload,
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )
  return data.count ? data.count : 0
}

const getSharedBoardsAdCount = async (boardId) => {
  const { data } = await AxiosConfig.post(
    '/ads/share/board/count',
    { boardId }
  )
  return data.count ? data.count : 0
}

const getPublicBoardAds = async (boardId, nextId, filters) => {
  const params = {
    // Create the filters
    ...createQuery(filters),
    next: nextId
  }

  const { data } = await AxiosConfig.get(
    `/ads/shared/board/${boardId}`,
    {
      params
    }
  )

  return data
}

const createBoardExportJob = async (boardId) => {
  // Pull the current token off the user
  const { Aa: token } = firebase.auth().currentUser

  const { data, status } = await AxiosConfig.post(
    `/ads/board/${boardId}/export`,
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )

  return { data, status }
}

const getBoardExportStatus = async (boardId) => {
  // Pull the current token off the user
  const { Aa: token } = firebase.auth().currentUser

  const { data, status } = await AxiosConfig.get(
    `/ads/board/${boardId}/export`,
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )

  return { data, status }
}

const getSharedAd = async (adId, sharedByQuery) => {
  const { data } = await AxiosConfig.get(`/ads/public/${adId}?user=${sharedByQuery}`)

  return data
}

// ====================================================================
// ============================ DISCOVERY =============================
// ====================================================================

const getDiscoveryAds = async (nextId, filters, includeSpyder = false, adsCancelToken = null) => {
  // Pull the current token off the user
  const { Aa: token } = firebase.auth().currentUser

  const params = includeSpyder
    ? { ...createQuery(filters), spyder: true, next: nextId }
    : { ...createQuery(filters), next: nextId }

  const { data } = await AxiosConfig.get(
    '/ads/discovery',
    {
      params,
      headers: {
        Authorization: `Bearer ${token}`
      },
      cancelToken: adsCancelToken
    }
  )

  return data
}

const getShuffledDiscoveryAds = async (nextId, filters) => {
  // Pull the current token off the user
  const { Aa: token } = firebase.auth().currentUser

  const params = {
    // Create the filters
    ...createQuery(filters),
    next: nextId
  }

  const { data } = await AxiosConfig.get(
    '/ads/shuffledDiscovery',
    {
      params,
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )

  return data
}

const setSpyderAdDiscoverable = async (adId) => {
  // Pull the current token off the user
  const { Aa: token } = firebase.auth().currentUser

  const { data } = await AxiosConfig.post(
    '/ads/set-discoverable',
    {
      adId
    },
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )

  return data
}

const getExpertAds = async (expertId, nextId, filters) => {
  const body = {
    expertUTM: expertId,
    query: {
      ...createQuery(filters),
      next: nextId
    }
  }

  const { data } = await AxiosConfig.post(
    '/ads/expert',
    body
  )

  return data
}

// ====================================================================
// ============================ SPYDER ================================
// ====================================================================

const getSpyderAds = async (brandId, nextId, filters, adsCancelToken = null) => {
  // Pull the current token off the user
  const { Aa: token } = firebase.auth().currentUser

  const params = {
    // Create the filters
    ...createQuery(filters),
    spyder: true,
    next: nextId
  }

  const { data } = await SpyderAxios.get(
    `/brands/${brandId}/ads`,
    {
      params,
      headers: {
        Authorization: `Bearer ${token}`
      },
      cancelToken: adsCancelToken
    }
  )

  return data
}

// TODO: /ads/community is a placeholder until the backend endpoint exists
const getSpyderCommunityAds = async (brandId, nextId, filters) => {
  // Pull the current token off the user
  const { Aa: token } = firebase.auth().currentUser

  const params = {
    // Create the filters
    brand_id: brandId,
    ...createQuery(filters),
    next: nextId
  }

  const { data } = await SpyderAxios.get(
    `/brands/${brandId}/placeholder-name`,
    {
      params,
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )

  return data
}

const getSpyderPartnershipAds = async (brandId, nextId, filters) => {
  // Pull the current token off the user
  const { Aa: token } = firebase.auth().currentUser

  // Extract the custom partner creators filter from the filters object
  const { 'or.partner_creators': partnerPageIds, ...otherFilters } = filters

  const params = {
    // Create the filters
    brand_id: brandId,
    ...(partnerPageIds?.length ? { partnerPageIds } : {}),
    ...createQuery(otherFilters),
    next: nextId
  }
  
  const { data } = await SpyderAxios.get(
    `/brands/${brandId}/partners/ads`,
    {
      params,
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )

  return data
}

// Helper for hook fetch. Get the most recent ad by startedRunning date.
const newestByStart = (ads) => {
  if (!Array.isArray(ads) || !ads.length) return null
  return ads.reduce((newest, ad) => {
    const t = +new Date(ad?.startedRunning)
    if (!Number.isFinite(t)) return newest
    const best = newest ? +new Date(newest.startedRunning) : -Infinity
    return t > best ? ad : newest
  }, null)
}

// Map results of hook fetch to expected shape
const mapHookGroups = (hookGroups) => {
  return hookGroups.map((group) => ({
    hookKey: group.hook,
    displayHook: group.display_hook,
    activeAds: group.active_ads || 0,
    inactiveAds: group.inactive_ads || 0,
    totalAds: group.total_ads ?? (group.active_ads || 0) + (group.inactive_ads || 0),
    longestRunningAd: group.longest_running_ad || null
  }))
}

// Fetch hook groups for a brand.
// Default is brandId only — pass `opts` (sort, filters, ranBetweenStart/End, etc.)
// ONLY when the user has actually applied a filter. Do not hardcode defaults like
// `transciptionOnly` or `'or.status': ['live']` here.
const getBrandHooks = async (brandId, options = {}, filters = {}) => {
  const { Aa: token } = firebase.auth().currentUser

  const { 
    hooks = null, // (string arr) Fetch only hooks with these exact keys
    hookSearch = null, // (string) Search for hooks with this text
    pageId = null, // Filter response to only a single page
    page = 1, 
    limit = 25 
  } = options

  const params = {
    brand_id: brandId,
    page,
    limit,
    ...(hooks ? { hooks } : {}),
    ...(hookSearch ? { hook_search: hookSearch } : {}),
    ...(pageId ? { advertiser_id: pageId } : {}),
  }
  if (filters && Object.keys(filters).length) {
    Object.assign(params, createQuery(filters))
  }

  const { data } = await SpyderAxios.get(
    `/brands/${brandId}/hooks`,
    {
      params,
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )

  const { data: hookGroups, pages: totalPages } = data

  return {
    results: mapHookGroups(hookGroups),
    totalPages
  }
}

// Fetch the ads  inside one hook group.
const getHookGroupAds = async (brandId, hookKey, options = {}, filters = {}) => {
  const { Aa: token } = firebase.auth().currentUser

  const { pageId = null, size = 25, next = null} = options

  const params = { 
    brand_id: brandId, 
    hook: hookKey,
    size,
    ...(next ? { next } : {}),
    ...(pageId ? { advertiser_id: pageId } : {}),
  }
  if (filters && Object.keys(filters).length) {
    Object.assign(params, createQuery(filters))
  }

  const { data } = await SpyderAxios.get(
    `/brands/${brandId}/hooks/ads`,
    {
      params,
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )

  return data
}

const getSpyderFeedAds = async (nextId, filters, brandIds = []) => {
  const token = await firebase.auth().currentUser.getIdToken()

  const params = {
    ...(brandIds.length ? { brand_ids: brandIds } : {}),
    ...createQuery(filters),
    next: nextId,
  }

  const { data } = await SpyderAxios.get(
    '/subscriptions/ads',
    {
      params,
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )

  return data
}

// ====================================================================
// ====================== ADVERTISEMENT DATA ==========================
// ====================================================================

const getUniqueAdById = async (uniqueAdId) => {
  // Pull the current token off the user
  const { Aa: token } = firebase.auth().currentUser

  const { data } = await AxiosConfig.get(`/ads/unique/${uniqueAdId}`, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  })

  return data
}

const getUniqueAdsByIds = async (uniqueAdIds) => {
  // Pull the current token off the user
  const { Aa: token } = firebase.auth().currentUser

  const { data } = await AxiosConfig.get(
    '/ads/discovery/batch',
    {
      params: {
        adIds: uniqueAdIds
      },
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )

  return data
}

const createTranscriptionJob = async (adId, manualUpload = false) => {
  // Pull the current token off the user
  const { Aa: token } = firebase.auth().currentUser

  const { data } = await AxiosConfig.post(
    '/transcriptions/ads',
    {
      adId,
      manualUpload
    },
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )

  return data
}

const refreshAdTranscript = async (docId, adId) => {
  // Pull the current token off the user
  const { Aa: token } = firebase.auth().currentUser

  const { data } = await AxiosConfig.post(
    `/ads/transcript/${docId}/refresh`,
    {
      adId
    },
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )

  return data
}

const getTagAdCounts = async () => {
  // Pull the current token off the user
  const { Aa: token } = firebase.auth().currentUser

  const { data } = await AxiosConfig.get(
    '/ads/tags/count',
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )

  return data.count ? data.count : 0
}

export default {
  // Swipefile
  save,
  getLibraryAds,
  getBoardAds,
  getBoardsAdCount,
  getSharedBoardsAdCount,
  getPublicBoardAds,
  createBoardExportJob,
  getBoardExportStatus,
  getSharedAd,

  // Discovery
  getDiscoveryAds,
  getShuffledDiscoveryAds,
  setSpyderAdDiscoverable,
  getExpertAds,

  // Spyder
  getSpyderAds,
  getSpyderCommunityAds,
  getSpyderPartnershipAds,
  getBrandHooks,
  getHookGroupAds,
  getSpyderFeedAds,

  // Advertisement data
  getUniqueAdById,
  getUniqueAdsByIds,
  createTranscriptionJob,
  refreshAdTranscript,
  getTagAdCounts
}